import {
  AfterViewChecked,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  ViewChild,
  ViewChildren,
  ViewContainerRef,
} from '@angular/core';
import { LMModel, LMModelStatusEvent } from './model/lm-model';
import { LMLoggerService } from '@services/lm-logger.service';
import { LMModelServiceAdapter } from '@form/model/lm-model-service-adapter';
import { LMFormPagingService } from '@form/services/lm-form-paging.service';
import { AppConstants } from '@utils/app.constants';
import { LMErrorUtilService } from '@services/utils/lm-error-util.service';
import { LMFormContentFormComponent } from './lm-form-content-form.component';
import { LMFormTransactionService } from './services/lm-form-transaction.service';
import { Observable, Observer, Subject }  from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { LMTransactionState } from '@transaction/lm-transaction-state';
import { LMUrlInfoBuilderService } from '@services/lm-url-info-builder.service';
import { LMGlobalUtilService } from '@utils/lm-global-util.service';

@Component({
  selector:    'lm-form-content-template',
  animations: [
    trigger('questionVisibility', [
      state('visible', style({
        height: '*',
        display: 'block'
      })),
      state('hidden', style({
        height: '0px',
        display: 'none'
      })),
      transition('visible => hidden', [
        animate('450ms ease-in')
      ]),
      transition('hidden => visible', [
        animate('450ms ease-out')
      ]),
    ]),
  ],
  templateUrl: 'lm-form-content-template.component.html',
  styleUrls:   [ 'lm-form-content-template.component.scss' ]
})

export class LMFormContentTemplateComponent implements OnInit, AfterViewChecked, OnDestroy {

  @ViewChildren('LMFormStandardComponent') children: QueryList<LMFormContentFormComponent>;
  @ViewChild('dialogPlaceholder', { read: ViewContainerRef, static: true }) placeholder: ViewContainerRef;

  @Input() displayPanelId: string;
  @Input() formUrlInfo: LMFormUrlInfo;
  @Input() formSpec: LMFormSpec;
  @Input() placeHolder: ViewContainerRef;

  @Input() set fromDataView(fromDataView: LMDataView) {
    this._fromDataView      = fromDataView;
    this.fromDataViewChanged   = true;
  }

  get fromDataView(): LMDataView {
    return this._fromDataView;
  }

  @Input() sourceDataView: LMDataView;          // Sub panel dataView
  @Input() formOptions;
  @Input() layout;
  @Input() model;
  @Input() specFields;
  @Input() dynamicChildVisibility: LMDynamicChildVisibility;
  @Input() displayWhenIndex: number;

  @Output() formTemplateLoaded = new EventEmitter<any>();
  @Output() formTemplateLoading = new EventEmitter<any>();
  @Output() contentModified = new EventEmitter<any>();
  @Output() fieldEvent: EventEmitter<LMFieldEvent> = new EventEmitter<LMFieldEvent>();

  private fromDataViewChanged = false;
  private _fromDataView: LMDataView;
  private templateDataViewSet: LMDataViewSet;
  private subscriptionManager: Subject<void> = new Subject<void>();

  public dataViewList: Array<LMDataView>;
  public templateRecordInfo: Array<TemplateRecordInfo> = [];
  public showEmptyMessage: boolean | false;
  public emptyMessage: SohoEmptyMessageOptions;

  constructor(
    private lmErrorUtilService: LMErrorUtilService,
    private changeDetectionRef: ChangeDetectorRef,
    private lmLoggerService: LMLoggerService,
    private lmModelServiceAdapter: LMModelServiceAdapter,
    private lmFormPagingService: LMFormPagingService,
    private lmFormTransactionService: LMFormTransactionService,
    private lmErrorMessageService: LMErrorUtilService,
    private lmUrlInfoBuilderService: LMUrlInfoBuilderService,
    private lmGlobalUtilService: LMGlobalUtilService
  ) {
  }

  ngOnInit(): void {
  }

  ngAfterViewChecked() {
    if (this.fromDataViewChanged) {
      this.fromDataViewChanged = false;
      if (this.fromDataView) {
        setTimeout(() => this.buildAndLoadTemplateRecord());
      }
    }
  }

  ngOnDestroy() {
    this.subscriptionManager.next();
    this.subscriptionManager.complete();
  }
  // events
  onContentModified(event: any) {
    this.contentModified.emit(event);
  }

  onFieldEvent(event) {
    this.fieldEvent.emit(event);
  }

  trackByModel(index: number, record: TemplateRecordInfo): LMModel {
    return record.model;
  }

  private buildAndLoadTemplateRecord(observer?: Observer<boolean>) {
    this.lmLoggerService.time({ id: 'lmformcontenttemplate fetchTemplateDataViewSet()' });
    this.formTemplateLoading.emit();

    this.lmFormPagingService.loadTemplateRecords(this.formUrlInfo, this.formSpec, this.fromDataView, this.sourceDataView, this.displayWhenIndex)
      .subscribe(
        (response: any) => {
          this.lmLoggerService.timeEnd('lmformcontenttemplate fetchTemplateDataViewSet()');
          if (response.status === AppConstants.STATUS_COMPLETED) {
            this.buildTemplateRecordModels(response);
          } else {
            this.handleError(response);
          }

          if (observer) {
            observer.next(response.status === AppConstants.STATUS_COMPLETED);
            observer.complete();
          }
        },
        (error: any) => {

          if (observer) {
            observer.next(true);
            observer.complete();
          }

          this.lmLoggerService.timeEnd('lmformcontenttemplate fetchTemplateDataViewSet()');
          this.lmLoggerService.error([ 'lmformcontenttemplate', 'fetchTemplateDataViewSet()', error ]);
          this.handleError(error);
        }
      );
  }

  private buildTemplateRecordModels(lmDataViewSet: LMDataViewSet) {
    this.templateDataViewSet = lmDataViewSet;
    this.templateRecordInfo = Object.assign([], []);
    let numberLabel = 0;
    let followUpNumber = 0;
    let lastParentTargetView: LMDataView = null;
    let lastParentTargetInfo: TemplateRecordInfo = null;

    const len = this.templateDataViewSet && this.templateDataViewSet.data ? this.templateDataViewSet.data.length : 0;

    if (len === 0) {
      this.setNoDataMessage();
    } else {
      this.clearEmptyMessage();
    }

    for (let index = 0; index < len; index++) {
      const templateView: LMDataView = this.templateDataViewSet.data[ index ];
      const targetDataView: LMDataView = templateView.dataView;
      const isFollowUpQuestion = this.isFollowUpQuestion(templateView);

      if (!isFollowUpQuestion) {
        lastParentTargetView = targetDataView;
        numberLabel++;
      }

      const info: TemplateRecordInfo = {
        templateView,
        model: this.createFormModel(templateView, targetDataView, numberLabel, index),
        isFollowUpQuestion,
        followUpQuestions: [],
        isQuestionVisible: isFollowUpQuestion ? this.isQuestionVisible(templateView, lastParentTargetView) : true,
      };

      if (!isFollowUpQuestion) {
        lastParentTargetInfo = info;
        followUpNumber = 0;
      } else {
        if (lastParentTargetInfo) {
          if (info.isQuestionVisible) {
            followUpNumber++;
          }
          info.model.numberLabel = lastParentTargetInfo.model.numberLabel + '.' + followUpNumber.toString(10);
          lastParentTargetInfo.followUpQuestions.push(info);
        } else {
          this.lmLoggerService.log('WARNING: Template Form that implemented dynamicChildVisibility has orphaned questions'
            + ' templateView.representativeText: ' + templateView.representativeText);
        }
      }

      this.addListeners(info);
      this.templateRecordInfo.push(info);
    }

    this.formTemplateLoaded.emit();
  }

  private addListeners(info: TemplateRecordInfo) {
    info.model.statusChanges.pipe(takeUntil(this.subscriptionManager)).subscribe((modelChangedEvent: LMModelStatusEvent) => {
      if (this.dynamicChildVisibility && !info.isFollowUpQuestion) {
        this.updateVisibleWhen(info);
      }
      this.contentModified.emit();
    });
  }

  private createFormModel(templateView: LMDataView, targetDataView: LMDataView, numberLabel: number, index: number): LMModel {
    // create a deep clone of the formSpec so we can add a unique namePrefix; see also lm-form-panel.service.ts
    // TODO: future consideration for refactoring: use formOptions to pass form / panel id so deep-cloning would not be needed
    const formSpec: LMFormSpec = JSON.parse(JSON.stringify(this.formSpec));
    formSpec.busFormSpec.namePrefix = this.displayPanelId + '_' + index;

    const formUrlInfo = this.lmUrlInfoBuilderService.getFormUrlInfoFromFormSpec(formSpec);
    const modelOptions: LMModelOptions = { formUrlInfo, formSpec };
    const model: LMModel = new LMModel(this.lmModelServiceAdapter, modelOptions);
    const isNumberedQuestion = !this.isFollowUpQuestion(templateView);

    model.numberLabel = isNumberedQuestion ? numberLabel.toString(10) : '';
    model.populate(this.specFields);
    model.setData(targetDataView, { notifyListeners: false });

    return model;
  }

  private getTemplateRecordModels(): Array<LMModel> {
    const templateRecordModels: LMModel[] = [];
    this.templateRecordInfo.forEach(info => {
      templateRecordModels.push(info.model);
    });
    return templateRecordModels;
  }

  private updateFromServer(dataViewSet: any) {
    this.templateDataViewSet = dataViewSet;

    this.templateRecordInfo.forEach((info, i) => {
      info.model.setData(this.templateDataViewSet.data[i].dataView, { notifyListeners: true } );
    });

    // force the angular templates to rebuild
    this.templateRecordInfo = Object.assign([], this.templateRecordInfo);
  }

  private isFollowUpQuestion(templateView: LMDataView): boolean {
    if (this.dynamicChildVisibility && templateView.fields.hasOwnProperty(this.dynamicChildVisibility.childDesignatorField)) {
      const childDesignatorFld: LMField = templateView.fields[ this.dynamicChildVisibility.childDesignatorField ];
      // another situation where it would be nice to have field type on LMField!
      // childDesignatorFld should be a String field - right?
      return childDesignatorFld.value !== '';
    }

    return false;
  }

  private isQuestionVisible(templateView: LMDataView, targetView: LMDataView): boolean {
    if (this.dynamicChildVisibility
      && templateView.fields.hasOwnProperty(this.dynamicChildVisibility.childDesignatorField)
        && targetView.fields.hasOwnProperty(this.dynamicChildVisibility.parentMatchField)) {
      const designatorFld: LMField = templateView.fields[ this.dynamicChildVisibility.childDesignatorField ];
      const matcherMutexFieldId = targetView.fields[ this.dynamicChildVisibility.parentMatchField ].mutexFieldId;
      const matcherFld = targetView.fields[ matcherMutexFieldId ];
      return designatorFld.value.toString() === matcherFld.value.toString();
    } else {
      return true;
    }
  }

  private updateVisibleWhen(info: TemplateRecordInfo): void {
    let followUpNumber = 0;
    info.followUpQuestions.forEach(dependentInfo => {
      const isVisible = this.isQuestionVisible(dependentInfo.templateView, info.model.getData());
      dependentInfo.isQuestionVisible = isVisible;

      if (isVisible) {
        followUpNumber++;
      }
      dependentInfo.model.numberLabel = info.model.numberLabel + '.' + followUpNumber.toString(10);
    });
  }

  public refresh(): Observable<boolean> {
    return new Observable<boolean>(observer => {
      this.buildAndLoadTemplateRecord();
      observer.next(true);
      observer.complete();
    });
  }

  public isDirty(): boolean {
    // eslint-disable-next-line @typescript-eslint/prefer-for-of
    for (let i = 0; i < this.templateRecordInfo.length; i++) {
      if (this.templateRecordInfo[ i ].model.isDirty) {
        return true;
      }
    }
    return false;
  }

  public save(observer: Observer<boolean>, skipToast?: boolean) {
    let subPanelResourceId;

    if (this.sourceDataView) {
      subPanelResourceId = this.sourceDataView.resourceId;
    }

    this.lmFormTransactionService.runSaveTemplateAction(
      this.formUrlInfo,
      this.formSpec,
      this.fromDataView,
      this.getTemplateRecordModels(),
      this.templateDataViewSet,
      this.displayPanelId,
      this.displayWhenIndex,
      subPanelResourceId
    ).subscribe((transactionState: LMTransactionState) => {
      let isSuccess = false;
      if (transactionState.isError) {
        this.lmErrorUtilService.handleError(
          transactionState.response.message,
          transactionState.response.details,
          transactionState.response.status,
          transactionState.response.message);
      } else if (transactionState.isCanceled) {
        // what to do?
      } else {
        const actionResult: LMActionResult = transactionState.getTransactionResult();
        // What to do?
        if (actionResult.status === AppConstants.STATUS_COMPLETED) {
          isSuccess = true;
          this.updateFromServer(actionResult.dataView);
          if (!skipToast) {
            const message = actionResult.message || Soho.Locale.translate('ActionSuccessful');
            this.lmGlobalUtilService.displayToastMessage('', message, AppConstants.TRA_ACTION_COMPLETED);
          }
          if (actionResult.completeIdentityOrigURL) {
            setTimeout(() => {
              this.lmGlobalUtilService.navigate(actionResult.completeIdentityOrigURL);
            }, 5);
          }
        } else if (actionResult.status === AppConstants.VIEW_EXCEPTION || actionResult.status === AppConstants.STATUS_SYSTEM_ERROR) {
          this.showTemplateFormError(actionResult);
        }

        observer.next(isSuccess);
        observer.complete();
      }
    });
  }

  private showTemplateFormError(actionResult: LMActionResult) {
    if (actionResult.status === AppConstants.STATUS_SYSTEM_ERROR || !actionResult.dataView) {
      this.lmErrorUtilService.handleError(actionResult.message, actionResult.details, actionResult.status, actionResult.message);
    } else {
      // eslint-disable-next-line @typescript-eslint/prefer-for-of
      for (let i = 0; i < this.templateRecordInfo.length; i++) {
        const targetModel = this.templateRecordInfo[ i ].model;

        if (actionResult.dataView && actionResult.dataView.resourceId === targetModel.resourceId) {
          this.handleTransactionStatus(targetModel, actionResult);
          break;
        }
      }
    }
  }

  private handleTransactionStatus(templateModel: LMModel, result: LMActionResult) {
    if (result.status === AppConstants.VIEW_EXCEPTION) {
      let fieldHasObservers = false;
      if (result.fieldId) {
        fieldHasObservers = templateModel.setFieldError(result.fieldId, result.message);
      }
      if (!fieldHasObservers) {
        this.lmErrorUtilService.handleError(result.message, result.details, result.status);
      }
    } else if (result.status !== AppConstants.STATUS_COMPLETED && result.status !== AppConstants.CONFIRMATION_REQUIRED) {
      // If the status is confirmation required, it may be due to the user cancelling out of runtime confirmation.
      this.lmErrorUtilService.handleError(result.message, result.details, result.status, result.message);
    }
  }

  private clearEmptyMessage() {
    this.showEmptyMessage = false;
    this.emptyMessage = {};
  }

  private setNoDataMessage() {
    this.showEmptyMessage = true;

    this.emptyMessage = {
      title: Soho.Locale.translate('NoDataAvailable'),
      icon: 'icon-empty-no-data',
      color: 'azure'
    };
  }

  private setErrorLoadingMessage(error: LMError) {
    this.showEmptyMessage = true;
    const title = error && error.title ? error.title : Soho.Locale.translate('UnexpectedErrorOccured');
    const message = error && error.message ? error.message : '';

    this.emptyMessage = {
      title,
      info: message,
      icon: 'icon-empty-error-loading',
      color: 'azure'
    };
  }

  private handleError(response: any) {
    this.setErrorLoadingMessage(response);
    this.lmErrorMessageService.handleUnexpectedError(response, this.placeholder);
  }
}

export interface TemplateRecordInfo {
  templateView: LMDataView;
  model: LMModel;
  isFollowUpQuestion: boolean;
  followUpQuestions: TemplateRecordInfo[],
  isQuestionVisible: boolean;
}
